import { useEffect, useState } from "react";
import { listQuestionVersions, QuestionVersionRow } from "@/api/methodologyConsole";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DateLabel,
  EmptyState,
  ErrorState,
  HashLabel,
  LifecycleBadge,
  LoadingState,
} from "./primitives";

const STATUSES = [
  "draft",
  "internal_review",
  "approved",
  "published",
  "deprecated",
  "superseded",
  "archived",
];

export default function QuestionsTab() {
  const [rows, setRows] = useState<QuestionVersionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = 25;

  useEffect(() => {
    setRows(null);
    setPage(0);
    listQuestionVersions(search || null, status === "all" ? null : status)
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  }, [search, status]);

  if (error) return <ErrorState message={error} />;

  const paged = (rows ?? []).slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <Input
          className="w-72"
          placeholder="Search question key or title"
          aria-label="Search question versions"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" aria-label="Filter by lifecycle status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!rows ? (
        <LoadingState label="Loading question versions…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No question versions"
          description="No versioned question matches the current filters."
        />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Question</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Content hash</TableHead>
                <TableHead>Published</TableHead>
                <TableHead>Used by</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.question_key}</TableCell>
                  <TableCell>{r.version_label}</TableCell>
                  <TableCell className="max-w-md truncate text-sm">{r.title ?? "—"}</TableCell>
                  <TableCell>
                    <LifecycleBadge status={r.status} />
                  </TableCell>
                  <TableCell>
                    <HashLabel value={r.content_hash} />
                  </TableCell>
                  <TableCell className="text-sm">
                    <DateLabel value={r.published_at} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.used_by_modules?.length ? r.used_by_modules.join(", ") : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, rows.length)} of{" "}
              {rows.length}
            </span>
            <div className="flex gap-2">
              <button
                className="rounded border px-2 py-1 disabled:opacity-50"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <button
                className="rounded border px-2 py-1 disabled:opacity-50"
                disabled={(page + 1) * pageSize >= rows.length}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
